import { useState, useEffect, type ReactNode } from 'react'

const TOKEN_KEY = 'tlc_token'

export default function LoginGate({ children }: { children: ReactNode }) {
  // Skip auth when env var is set (dev or production)
  if (import.meta.env.VITE_SKIP_AUTH === 'true') {
    return <>{children}</>
  }

  return <LoginGateInner>{children}</LoginGateInner>
}

function LoginGateInner({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(false)
  const [checking, setChecking] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Check existing token on mount with 3s timeout
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      setChecking(false)
      return
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000)

    fetch('/api/auth/check', {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((r) => {
        if (r.ok) setAuthed(true)
        else localStorage.removeItem(TOKEN_KEY)
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => {
        clearTimeout(timeout)
        setChecking(false)
      })
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      const data = await res.json()

      if (data.ok && data.token) {
        localStorage.setItem(TOKEN_KEY, data.token)
        setAuthed(true)
      } else {
        setError('Invalid credentials')
      }
    } catch {
      setError('Connection error')
    } finally {
      setSubmitting(false)
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="w-5 h-5 border-2 border-[var(--border-default)] border-t-[var(--text-muted)] rounded-full animate-spin" />
      </div>
    )
  }

  if (authed) {
    return <>{children}</>
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-primary)' }}>
      <div className="w-full max-w-sm">
        {/* Minimal branding */}
        <div className="text-center mb-12">
          <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-ghost)]">
            Coming Soon
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            autoFocus
            className="w-full px-4 py-3 bg-[var(--bg-surface-1)] border border-[var(--border-subtle)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-ghost)] focus:outline-none focus:border-[var(--border-default)] transition-colors"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="w-full px-4 py-3 bg-[var(--bg-surface-1)] border border-[var(--border-subtle)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-ghost)] focus:outline-none focus:border-[var(--border-default)] transition-colors"
          />

          {error && (
            <p className="text-xs text-red-400/70 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-[var(--bg-surface-2)] border border-[var(--border-subtle)] rounded-lg text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)] transition-all disabled:opacity-50"
          >
            {submitting ? '...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-16 text-center">
          <div className="w-12 h-px mx-auto" style={{ background: 'var(--border-subtle)' }} />
        </div>
      </div>
    </div>
  )
}
